import type { ComputedRef, Ref } from 'vue'
import { computed, ref } from 'vue'

import type { ReportDetail, ReportDictionaryItem, ReportTaskListItem } from '@/api/schemas/reports/common'
import type {
    OperatorReportingInterruptedReport,
    OperatorReportingOrderJobListItem,
    OperatorReportingReportItemOrder,
    OperatorReportingReportItemOrdersFilters,
    OperatorReportingSession,
    OperatorReportingShippingGroup,
    OperatorReportingWorkspace,
    OperatorReportingWorkspaceListItem
} from '@/api/schemas/reports/operatorReporting'
import { REPORTS } from '@/constants'

import { createReportingParameterColumnsState, type ReportingParameterColumnsState } from '../shared/reportingParameterColumns'

export function createInitialReportItemOrdersFilters(): OperatorReportingReportItemOrdersFilters {
    return {
        shippingGroupCodes: REPORTS.OPERATOR_REPORTING.DEFAULT_SHIPPING_GROUP_CODES.join(',')
    }
}

// The report open on the active report screen, while it runs. Operator actions started there switch to a new process report
// through the active report, which finishes the running one first.
export type OperatorRunningReport = {
    processCodeId: number | null
    isProcessReport: boolean
}

export type ProcessReportSwitchRequest = {
    processCodeId: number
    manualJobNumber?: string
    manualImpositionNumber?: string
    openAccidentForm?: boolean
}

export type OperatorReportingState = ReportingParameterColumnsState & {
    availableWorkspaces: Ref<OperatorReportingWorkspaceListItem[]>
    availableWorkspacesLoading: Ref<boolean>
    bootstrapLoading: Ref<boolean>
    selectedWorkspaceId: Ref<number | null>
    selectedWorkspace: Ref<OperatorReportingWorkspace | null>
    activeSession: Ref<OperatorReportingSession | null>
    unfinishedReport: Ref<OperatorReportingInterruptedReport | null>
    interruptedReports: Ref<ReportDetail[]>
    interruptedReportsLoading: Ref<boolean>
    reportOrderJobs: Ref<OperatorReportingOrderJobListItem[]>
    reportOrderJobsLoading: Ref<boolean>
    shippingGroups: Ref<OperatorReportingShippingGroup[]>
    reportItemOrders: Ref<OperatorReportingReportItemOrder[]>
    reportItemOrdersLoading: Ref<boolean>
    reportItemOrdersFilters: Ref<OperatorReportingReportItemOrdersFilters>
    scheduledTasks: Ref<ReportTaskListItem[]>
    scheduledTasksLoading: Ref<boolean>
    taskSearchResults: Ref<ReportTaskListItem[]>
    taskSearchLoading: Ref<boolean>
    reportProcesses: Ref<ReportDictionaryItem[]>
    outOfSequenceCodes: Ref<ReportDictionaryItem[]>
    selectedTask: Ref<ReportTaskListItem | null>
    taskSearch: Ref<string | null>
    planChangesVersion: Ref<number>
    operatorActionsDialogOpen: Ref<boolean>
    runningReport: Ref<OperatorRunningReport | null>
    processReportSwitchRequest: Ref<ProcessReportSwitchRequest | null>
    accidentFormRequested: Ref<boolean>
    operatorActionsAvailable: ComputedRef<boolean>
}

export function createOperatorReportingState(): OperatorReportingState {
    const availableWorkspaces = ref<OperatorReportingWorkspaceListItem[]>([])
    const bootstrapLoading = ref(false)
    const selectedWorkspaceId = ref<number | null>(null)
    const selectedWorkspace = ref<OperatorReportingWorkspace | null>(null)
    const activeSession = ref<OperatorReportingSession | null>(null)
    const unfinishedReport = ref<OperatorReportingInterruptedReport | null>(null)

    const operatorActionsAvailable = computed<boolean>(() => {
        const workspaceId = selectedWorkspaceId.value
        const occupancy = selectedWorkspace.value?.machine?.occupancy

        return Boolean(
            !bootstrapLoading.value &&
            activeSession.value &&
            workspaceId &&
            availableWorkspaces.value.some((workspace) => workspace.id === workspaceId) &&
            selectedWorkspace.value?.allowedReportItemTypes?.length &&
            !(occupancy?.isLocked && !occupancy.user?.isItMe) &&
            !unfinishedReport.value?.productionSiteId
        )
    })

    return {
        availableWorkspaces,
        availableWorkspacesLoading: ref(false),
        bootstrapLoading,
        selectedWorkspaceId,
        selectedWorkspace,
        activeSession,
        unfinishedReport,
        interruptedReports: ref<ReportDetail[]>([]),
        interruptedReportsLoading: ref(false),
        reportOrderJobs: ref<OperatorReportingOrderJobListItem[]>([]),
        reportOrderJobsLoading: ref(false),
        shippingGroups: ref<OperatorReportingShippingGroup[]>([]),
        reportItemOrders: ref<OperatorReportingReportItemOrder[]>([]),
        reportItemOrdersLoading: ref(false),
        reportItemOrdersFilters: ref<OperatorReportingReportItemOrdersFilters>(createInitialReportItemOrdersFilters()),
        scheduledTasks: ref<ReportTaskListItem[]>([]),
        scheduledTasksLoading: ref(false),
        ...createReportingParameterColumnsState(),
        taskSearchResults: ref<ReportTaskListItem[]>([]),
        taskSearchLoading: ref(false),
        reportProcesses: ref<ReportDictionaryItem[]>([]),
        outOfSequenceCodes: ref<ReportDictionaryItem[]>([]),
        selectedTask: ref<ReportTaskListItem | null>(null),
        taskSearch: ref<string | null>(null),
        planChangesVersion: ref(0),
        operatorActionsDialogOpen: ref(false),
        runningReport: ref<OperatorRunningReport | null>(null),
        processReportSwitchRequest: ref<ProcessReportSwitchRequest | null>(null),
        accidentFormRequested: ref(false),
        operatorActionsAvailable
    }
}
